import { all, call, delay, put, takeEvery } from "redux-saga/effects";
import axios from "axios";
import { API_URL } from "../../utils/constants";
import refundActions from "./actions";
import commonActions from "../common/actions";

const refundSagas = function* () {
    yield all([
        yield takeEvery(refundActions.GET_REFUND_LIST, getRefundList),
        yield takeEvery(refundActions.EXPORT_REFUND_LIST, exportRefundList),
        yield takeEvery(refundActions.MARK_REFUNDED, markRefunded),
        yield takeEvery(refundActions.PREVIEW_BULK_REFUND, previewBulkRefund),
        yield takeEvery(refundActions.CONFIRM_BULK_REFUND, confirmBulkRefund)
    ]);
};

// The refund endpoints answer a rejected write with a real HTTP status (400 for bad rows, 409
// when the list moved underneath the user), which axios turns into a thrown error rather than a
// result. Both paths carry the same body, so unwrapping them the same way is what lets the row
// errors reach the dialog instead of being swallowed as a network failure.
const responseBody = (result, err) => result?.data || err?.response?.data || {};

const showAlert = function* (status, message) {
    yield put({
        type: commonActions.SET_ALERT,
        payload: { status, show: true, message }
    });
    yield delay(2000);
    yield put({
        type: commonActions.SET_ALERT,
        payload: { status: null, show: false, message: null }
    });
};

// Blank filters are dropped rather than sent as empty strings: the API treats an absent
// parameter as "no filter", and Joi would reject an empty courseid.
const buildParams = (filters = {}) => {
    const params = {};
    if (filters.courseid) params.courseid = filters.courseid;
    if (filters.search?.trim()) params.search = filters.search.trim();
    if (filters.fromdate) params.fromdate = filters.fromdate;
    if (filters.todate) params.todate = filters.todate;
    return params;
};

const getRefundList = function* (data) {
    const { payload } = data;
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
    try {
        const result = yield call(() =>
            axios.get(`${API_URL}/backoffice/refund/list`, {
                params: {
                    ...buildParams(payload),
                    page: payload?.page || 1,
                    limit: payload?.limit || 25
                }
            })
        );

        // 204 means the filters matched nothing -- an empty worklist, not a failure. Clearing the
        // list matters more than the message: a stale table under new filters reads as a result.
        yield put({
            type: refundActions.SET_REFUND_LIST,
            payload: result?.data?.statusCode === 200
                ? { result: result.data.result, pagination: result.data.pagination }
                : { result: [], pagination: { page: 1, limit: payload?.limit || 25, total: 0, totalpages: 0 } }
        });
    } catch (err) {
        yield put({ type: refundActions.SET_REFUND_LIST, payload: { result: [] } });
        yield* showAlert("failed", responseBody(null, err)?.message || "Could not load the refund list");
    }
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
};

const exportRefundList = function* (data) {
    const { payload } = data;
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
    try {
        const response = yield call(() =>
            axios.get(`${API_URL}/backoffice/refund/export`, {
                params: buildParams(payload),
                responseType: "blob"
            })
        );

        // Under responseType "blob" both outcomes arrive as a Blob: the CSV, and the JSON body
        // the API sends when nothing matched. Sniffing the Blob's own type tells them apart --
        // without it an empty export silently downloads a file containing an error object.
        const isJson = (response?.data?.type || "").includes("application/json");
        if (isJson) {
            const text = yield call(() => response.data.text());
            const body = JSON.parse(text);
            yield* showAlert("failed", body?.message || "No records to export");
        } else {
            const csvText = yield call(() => response.data.text());
            const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `manual-refund-list-${Date.now()}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }
    } catch (err) {
        yield* showAlert("failed", "Could not export the refund list");
    }
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
};

const markRefunded = function* (data) {
    const { payload } = data;
    yield put({ type: commonActions.SET_BUTTON_LOADER, payload: true });
    try {
        let result;
        let err;
        try {
            result = yield call(() =>
                axios.post(`${API_URL}/backoffice/refund/mark-refunded`, { refunds: payload?.refunds })
            );
        } catch (e) {
            err = e;
        }

        const body = responseBody(result, err);

        if (body?.statusCode === 200) {
            yield put({ type: refundActions.SET_MARK_REFUND_ERRORS, payload: [] });
            payload?.onSuccess?.();
            // The refunded rows drop out of the worklist, so the table behind the dialog is now
            // wrong -- refetch under the filters the user still has applied.
            yield put({ type: refundActions.GET_REFUND_LIST, payload: payload?.filters });
            yield* showAlert("success", body?.message);
        } else {
            // Nothing was written: the API rejects the whole selection if any row fails, so these
            // errors explain the entire outcome rather than a partial one.
            yield put({ type: refundActions.SET_MARK_REFUND_ERRORS, payload: body?.errors || [] });
            yield* showAlert("failed", body?.message || "Could not record the refunds");
        }
    } catch (e) {
        yield* showAlert("failed", "Could not record the refunds");
    }
    yield put({ type: commonActions.SET_BUTTON_LOADER, payload: false });
};

const previewBulkRefund = function* (data) {
    const { payload } = data;
    yield put({ type: commonActions.SET_BUTTON_LOADER, payload: true });
    try {
        const formData = new FormData();
        formData.append("refundfile", payload?.file, payload?.file?.name);

        let result;
        let err;
        try {
            result = yield call(() =>
                axios.post(`${API_URL}/backoffice/refund/bulk-upload/preview`, formData)
            );
        } catch (e) {
            err = e;
        }

        const body = responseBody(result, err);

        if (body?.statusCode === 200) {
            yield put({ type: refundActions.SET_BULK_REFUND_PREVIEW, payload: body });
        } else {
            // A file that could not be read at all (wrong header, no rows) has no per-row detail
            // to show, so the dry run stays empty and the message carries the reason.
            yield put({ type: refundActions.SET_BULK_REFUND_PREVIEW, payload: null });
            yield* showAlert("failed", body?.message || "Could not read the file");
        }
    } catch (e) {
        yield put({ type: refundActions.SET_BULK_REFUND_PREVIEW, payload: null });
        yield* showAlert("failed", "Could not read the file");
    }
    yield put({ type: commonActions.SET_BUTTON_LOADER, payload: false });
};

const confirmBulkRefund = function* (data) {
    const { payload } = data;
    yield put({ type: commonActions.SET_BUTTON_LOADER, payload: true });
    try {
        let result;
        let err;
        try {
            result = yield call(() =>
                axios.post(`${API_URL}/backoffice/refund/bulk-upload/confirm`, { token: payload?.token })
            );
        } catch (e) {
            err = e;
        }

        const body = responseBody(result, err);

        if (body?.statusCode === 200) {
            yield put({ type: refundActions.CLEAR_BULK_REFUND_PREVIEW });
            payload?.onSuccess?.();
            yield put({ type: refundActions.GET_REFUND_LIST, payload: payload?.filters });
            yield* showAlert("success", body?.message);
        } else {
            // The confirm re-checks every row against the live table, so a rejection here means
            // the list changed since the preview. Replacing the preview's errors with these keeps
            // the dialog showing why, and the user re-uploads rather than retrying a stale token.
            yield put({
                type: refundActions.SET_BULK_REFUND_PREVIEW,
                payload: { ...(payload?.preview || {}), errors: body?.errors || [], token: null }
            });
            yield* showAlert("failed", body?.message || "Could not record the refunds");
        }
    } catch (e) {
        yield* showAlert("failed", "Could not record the refunds");
    }
    yield put({ type: commonActions.SET_BUTTON_LOADER, payload: false });
};

export default refundSagas;
